import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Autoplay } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { getAllHomeCarouselItems } from '../../services/homeCarousel.util';

const defaultThemeColor = '#F5C14B';
const hexColorPattern = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

const normalizeThemeColor = (color) => {
  const normalizedColor = typeof color === 'string' ? color.trim() : '';
  const hex = hexColorPattern.test(normalizedColor) ? normalizedColor : defaultThemeColor;
  const expandedHex = hex.length === 4
    ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
    : hex;
  const colorValue = expandedHex.slice(1);
  const rgb = [
    parseInt(colorValue.slice(0, 2), 16),
    parseInt(colorValue.slice(2, 4), 16),
    parseInt(colorValue.slice(4, 6), 16),
  ].join(', ');

  return {
    hex: expandedHex.toUpperCase(),
    rgb,
  };
};

const getAccentRgba = (theme, alpha) => `rgba(${theme.rgb}, ${alpha})`;
const unsafeScriptProtocol = ['java', 'script:'].join('');

const sanitizeCarouselHtml = (html) => {
  if (typeof html !== 'string' || !html.trim()) return '';
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') return '';

  const parser = new window.DOMParser();
  const parsedDocument = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const wrapper = parsedDocument.body.firstElementChild;
  const blockedSelectors = 'script, iframe, object, embed, link, meta';

  if (!wrapper) return '';

  wrapper.querySelectorAll(blockedSelectors).forEach((element) => {
    element.remove();
  });

  wrapper.querySelectorAll('*').forEach((element) => {
    Array.from(element.attributes).forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const value = attribute.value.trim().toLowerCase();

      if (
        name.startsWith('on') ||
        ((name === 'href' || name === 'src') && value.startsWith(unsafeScriptProtocol))
      ) {
        element.removeAttribute(attribute.name);
      }
    });
  });

  return wrapper.innerHTML;
};

const formatPrice = (price) => {
  const amount = Number(price);
  if (!amount) return null;

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
};

const getPricing = (item) => {
  if (!item) {
    return {
      purchasePrice: null,
      originalPrice: null,
    };
  }

  if (item.isFree) {
    return {
      purchasePrice: 'Free',
      originalPrice: null,
    };
  }

  if (item.isPurchasable === false) {
    return {
      purchasePrice: null,
      originalPrice: null,
    };
  }

  return {
    purchasePrice: formatPrice(item.discountPrice || item.price),
    originalPrice: item.discountPrice && item.price ? formatPrice(item.price) : null,
  };
};

const itemTypeLabels = {
  course: 'Course',
  material: 'Materials',
  mockTest: 'Mock Tests',
  testPaper: 'Test Papers',
  custom: 'Featured',
};

const getCarouselEntity = (item) => {
  if (item?.itemType === 'material') return item.material;
  if (item?.itemType === 'mockTest' || item?.itemType === 'testPaper') return item.mockTest;
  if (item?.itemType === 'course') return item.exam;

  return item?.exam || item?.material || item?.mockTest || null;
};

const getConfiguredRoute = (item) => {
  if (item?.exam?.slug) {
    return `/exams?course=${encodeURIComponent(item.exam.slug)}`;
  }

  if (item?.itemType === 'material' && item.material?.slug) {
    return `/materials?material=${encodeURIComponent(item.material.slug)}`;
  }

  if ((item?.itemType === 'mockTest' || item?.itemType === 'testPaper') && item.mockTest?.slug) {
    return `/mock-tests?mockTest=${encodeURIComponent(item.mockTest.slug)}`;
  }

  if (item?.route) return item.route;

  return null;
};

const getConfiguredPricing = (item) => {
  if (item?.itemType === 'testPaper' && !item.mockTest && item.exam) {
    return getPricing({
      ...item.exam,
      price: item.exam.testsOnlyPrice,
      discountPrice: item.exam.testsOnlyDiscountPrice,
      isPurchasable: Boolean(item.exam.testsOnlyPrice),
    });
  }

  return getPricing(getCarouselEntity(item));
};

const getConfiguredHighlights = (item, entity) => {
  if (Array.isArray(item?.highlights) && item.highlights.length > 0) {
    return item.highlights.filter(Boolean);
  }

  if (Array.isArray(entity?.features) && entity.features.length > 0) {
    return entity.features.slice(0, 3);
  }

  return [
    itemTypeLabels[item?.itemType] || 'Featured',
    'Expert Curated',
    'gvkEDU',
  ];
};

const mapConfiguredCarouselItem = (item) => {
  const entity = getCarouselEntity(item);
  const pricing = getConfiguredPricing(item);

  return {
    id: item._id,
    title: item.title || entity?.title || 'Featured Item',
    category: item.category || itemTypeLabels[item.itemType] || 'Featured',
    subtitle: item.subtitle || entity?.description || 'Explore this featured learning resource.',
    route: getConfiguredRoute(item),
    cta: item.cta || 'Explore Now',
    examTitle: item.exam?.title || entity?.title || item.title,
    theme: normalizeThemeColor(item.themeColor),
    highlights: getConfiguredHighlights(item, entity),
    customHtml: item.itemType === 'custom' ? sanitizeCarouselHtml(item.customHtml) : '',
    ...pricing,
  };
};

export default function WhyCourse() {
  const navigate = useNavigate();
  const carouselSwiperRef = useRef(null);
  const [activeCarouselIndex, setActiveCarouselIndex] = useState(0);
  const [homeCarouselItems, setHomeCarouselItems] = useState([]);
  const [carouselLoading, setCarouselLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadCarousel = async () => {
      setCarouselLoading(true);

      try {
        const response = await getAllHomeCarouselItems();
        const configuredItems = response?.success ? response.data || [] : [];

        if (!isMounted) return;

        if (configuredItems.length > 0) {
          setHomeCarouselItems(configuredItems);
          setActiveCarouselIndex(0);
          return;
        }

        setHomeCarouselItems([]);
      } catch (error) {
        console.error('Error loading configured home carousel:', error);
        if (isMounted) {
          setHomeCarouselItems([]);
        }
      } finally {
        if (isMounted) {
          setCarouselLoading(false);
        }
      }
    };

    loadCarousel();

    return () => {
      isMounted = false;
    };
  }, []);

  const carouselCards = homeCarouselItems.map(mapConfiguredCarouselItem);

  const cards = [
    {
      id: 1,
      title: 'BOOKS',
      subtitle: 'Best sellers | State-wise | PAN-India',
      route: '/books',
    },
    {
      id: 2,
      title: 'COURSES',
      subtitle: 'Complete | Subject-wise | Recorded',
      route: '/exams',
    },
    {
      id: 3,
      title: 'MATERIALS',
      subtitle: 'Concise | High Yield | exam-led',
      route: '/materials',
    },
    {
      id: 4,
      title: 'TEST PAPERS',
      subtitle: 'MCQs | PYQs | Mocks',
      route: '/mock-tests',
    },
    {
      id: 5,
      title: 'FREE DOWNLOADS',
      subtitle: 'Jobs | Syllabus | Updates',
      route: '/freedownloads',
    },
  ];

  const handleCardClick = (route) => {
    if (!route) return;
    navigate(route);
  };

  return (
    <section 
      className='py-5'
      style={{
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated background blobs */}
      <div
        style={{
          position: 'absolute',
          top: '-10%',
          left: '-5%',
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(200, 200, 200, 0.08) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(80px)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: '-10%',
          right: '-5%',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(150, 150, 150, 0.06) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(100px)',
        }}
      />

      <div className='container' style={{ position: 'relative', zIndex: 1 }}>
        {carouselCards.length > 0 && (
          <div
            className='home-carousel-wrap'
            style={{
              maxWidth: '1200px',
              margin: '0 auto',
              padding: '2rem 1rem 0',
              position: 'relative',
            }}
          >
            <Swiper
            modules={[Autoplay]}
            loop={carouselCards.length > 1}
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
            }}
            speed={800}
            spaceBetween={24}
            slidesPerView={1}
            onSwiper={(swiper) => {
              carouselSwiperRef.current = swiper;
            }}
            onSlideChange={(swiper) => {
              setActiveCarouselIndex(swiper.realIndex);
            }}
            breakpoints={{
              576: {
                slidesPerView: 1,
              },
              992: {
                slidesPerView: 1,
              },
            }}
          >
            {carouselCards.map((card) => (
              <SwiperSlide key={card.id} className='home-carousel-slide'>
                {card.customHtml ? (
                  <div
                    className='home-carousel-custom-card'
                    aria-label={card.title}
                    style={{
                      '--home-carousel-accent': card.theme.hex,
                      '--home-carousel-accent-rgb': card.theme.rgb,
                    }}
                    dangerouslySetInnerHTML={{ __html: card.customHtml }}
                  />
                ) : (
                <div
                  className='glass-card home-carousel-card'
                  onClick={() => handleCardClick(card.route)}
                  role='button'
                  tabIndex={0}
                  aria-disabled={!card.route}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleCardClick(card.route);
                    }
                  }}
                  style={{
                    '--home-carousel-accent': card.theme.hex,
                    '--home-carousel-accent-rgb': card.theme.rgb,
                    position: 'relative',
                    maxWidth: '760px',
                    minHeight: '270px',
                    height: '100%',
                    margin: '0 auto',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    background: 'radial-gradient(circle at 85% 0%, rgba(255, 255, 255, 0.16) 0%, transparent 32%), linear-gradient(135deg, rgba(18, 18, 24, 0.98) 0%, rgba(8, 8, 12, 0.96) 100%)',
                    backdropFilter: 'blur(18px) saturate(150%)',
                    WebkitBackdropFilter: 'blur(18px) saturate(150%)',
                    padding: '2rem 2.25rem',
                    borderRadius: '22px',
                    border: `1px solid ${getAccentRgba(card.theme, 0.52)}`,
                    boxShadow: `0 24px 60px rgba(0, 0, 0, 0.42), 0 0 24px ${getAccentRgba(card.theme, 0.18)}, inset 0 1px 0 ${getAccentRgba(card.theme, 0.22)}`,
                    cursor: 'pointer',
                    overflow: 'hidden',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <div
                    className='shine-effect'
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                      transition: 'left 0.5s ease',
                      pointerEvents: 'none',
                    }}
                  />

                  <div style={{ position: 'relative' }}>
                    <p
                      className='home-carousel-category'
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.45rem',
                        marginBottom: '0.75rem',
                        padding: '0.35rem 0.85rem',
                        borderRadius: '999px',
                        border: `1px solid ${getAccentRgba(card.theme, 0.55)}`,
                        background: `linear-gradient(135deg, ${getAccentRgba(card.theme, 0.22)} 0%, rgba(255, 255, 255, 0.04) 100%)`,
                        boxShadow: `0 0 18px ${getAccentRgba(card.theme, 0.28)}, inset 0 1px 0 ${getAccentRgba(card.theme, 0.35)}`,
                        color: card.theme.hex,
                        fontSize: '0.72rem',
                        fontWeight: '800',
                        letterSpacing: '0.18em',
                        lineHeight: '1.35',
                        textTransform: 'uppercase',
                        textShadow: `0 0 12px ${getAccentRgba(card.theme, 0.55)}`,
                      }}
                    >
                      <span
                        aria-hidden='true'
                        style={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          background: card.theme.hex,
                          boxShadow: `0 0 8px ${getAccentRgba(card.theme, 0.9)}`,
                          flexShrink: 0,
                        }}
                      />
                      {card.category}
                    </p>
                    <h2
                      style={{
                        color: '#FFFFFF',
                        fontSize: 'clamp(1.45rem, 3vw, 2.4rem)',
                        fontWeight: '850',
                        lineHeight: '1.05',
                        letterSpacing: '0.01em',
                        marginBottom: '0.8rem',
                        textTransform: 'uppercase',
                        textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                      }}
                    >
                      {card.title}
                    </h2>
                    <p
                      style={{
                        color: 'rgba(255, 255, 255, 0.85)',
                        fontSize: '1rem',
                        lineHeight: '1.5',
                        maxWidth: '640px',
                        margin: 0,
                      }}
                    >
                      {card.subtitle}
                    </p>
                    <div
                      className='home-course-highlights'
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                        gap: '0.5rem',
                        maxWidth: '640px',
                        marginTop: '1rem',
                      }}
                    >
                      {card.highlights.map((highlight) => (
                        <div
                          key={highlight}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            minHeight: '42px',
                            padding: '0.55rem 0.7rem',
                            borderRadius: '8px',
                            border: '1px solid rgba(255, 255, 255, 0.18)',
                            background: 'rgba(255, 255, 255, 0.055)',
                          }}
                        >
                          <span
                            style={{
                              color: 'rgba(255, 255, 255, 0.84)',
                              fontSize: '0.76rem',
                              fontWeight: '650',
                              lineHeight: '1.2',
                            }}
                          >
                            {highlight}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className='home-carousel-footer'>
                    {card.purchasePrice && !carouselLoading ? (
                      <div
                        className='home-special-price'
                        style={{
                          '--home-special-accent': card.theme.hex,
                          '--home-special-accent-rgb': card.theme.rgb,
                          position: 'relative',
                          flexShrink: 0,
                          width: '108px',
                          height: '108px',
                          display: 'grid',
                          placeItems: 'center',
                          transition: 'transform 0.3s ease, filter 0.3s ease',
                          filter: `drop-shadow(0 4px 10px rgba(0, 0, 0, 0.35))`,
                        }}
                      >
                        <div
                          aria-hidden='true'
                          className='home-special-price-spinner'
                          style={{
                            position: 'absolute',
                            inset: 0,
                          }}
                        >
                          <span
                            className='home-special-price-burst'
                            style={{
                              position: 'absolute',
                              inset: '4%',
                              background: `linear-gradient(160deg, #ffe9a8 0%, ${card.theme.hex} 48%, #9a7518 100%)`,
                              clipPath: 'polygon(50% 0%, 61% 12%, 78% 8%, 76% 24%, 92% 28%, 84% 42%, 100% 50%, 84% 58%, 92% 72%, 76% 76%, 78% 92%, 61% 88%, 50% 100%, 39% 88%, 22% 92%, 24% 76%, 8% 72%, 16% 58%, 0% 50%, 16% 42%, 8% 28%, 24% 24%, 22% 8%, 39% 12%)',
                              opacity: 0.92,
                            }}
                          />
                        </div>
                        <span
                          aria-hidden='true'
                          className='home-special-price-core'
                          style={{
                            position: 'absolute',
                            inset: '18%',
                            borderRadius: '50%',
                            background: '#111111',
                            border: `2px solid ${card.theme.hex}`,
                            boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.08)',
                          }}
                        />
                        <div
                          className='home-special-price-content'
                          style={{
                            position: 'relative',
                            zIndex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.14rem',
                            textAlign: 'center',
                            padding: '0.2rem',
                            width: '62%',
                          }}
                        >
                          <span
                            className='home-special-price-label'
                            style={{
                              color: '#FFD76A',
                              fontSize: '0.62rem',
                              fontWeight: '900',
                              letterSpacing: '0.1em',
                              lineHeight: 1,
                              textTransform: 'uppercase',
                            }}
                          >
                            Offer
                          </span>
                          <span
                            className='home-special-price-current'
                            style={{
                              color: '#FFFFFF',
                              fontSize: '1.12rem',
                              fontWeight: '900',
                              letterSpacing: '-0.03em',
                              lineHeight: 1,
                            }}
                          >
                            {card.purchasePrice}
                          </span>
                          {card.originalPrice && (
                            <span
                              className='home-special-price-original'
                              style={{
                                color: 'rgba(255, 255, 255, 0.78)',
                                fontSize: '0.62rem',
                                fontWeight: '650',
                                textDecoration: 'line-through',
                                lineHeight: 1,
                              }}
                            >
                              {card.originalPrice}
                            </span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <span />
                    )}

                    <span
                      className='home-carousel-cta'
                      style={{
                        position: 'relative',
                        color: '#FFFFFF',
                        fontSize: '0.95rem',
                        fontWeight: '700',
                        padding: '0.75rem 1.35rem',
                        borderRadius: '10px',
                        border: '1px solid rgba(255, 255, 255, 0.38)',
                        background: 'rgba(255, 255, 255, 0.06)',
                        boxShadow: '0 0 24px rgba(255, 255, 255, 0.12)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {card.cta} -&gt;
                    </span>
                  </div>
                </div>
                )}
              </SwiperSlide>
            ))}
            </Swiper>
            <div className='home-carousel-status' aria-label='Carousel slide status'>
              {carouselCards.map((card, index) => (
                <button
                  key={card.id}
                  type='button'
                  className={`home-carousel-dot ${activeCarouselIndex === index ? 'is-active' : ''}`}
                  aria-label={`Show ${card.title} carousel card`}
                  aria-current={activeCarouselIndex === index ? 'true' : undefined}
                  onClick={() => {
                    carouselSwiperRef.current?.slideToLoop(index);
                  }}
                />
              ))}
            </div>
          </div>
        )}

        <div
          className='cards-container'
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2rem',
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '2rem 1rem',
          }}
        >
          {cards.map((card, index) => (
            <div
              key={card.id}
              className='glass-card'
              data-aos='fade-up'
              data-aos-duration='600'
              data-aos-delay={index * 100}
              onClick={() => handleCardClick(card.route)}
              style={{
                position: 'relative',
                background: 'linear-gradient(135deg, rgba(45, 45, 45, 1) 0%, rgba(35, 35, 35, 1) 100%)',
                backdropFilter: 'blur(15px) saturate(150%)',
                WebkitBackdropFilter: 'blur(15px) saturate(150%)',
                padding: '2.5rem 2.5rem',
                borderRadius: '12px',
                textAlign: 'center',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 10px 25px rgba(255, 255, 255, 0.2), 0 5px 12px rgba(255, 255, 255, 0.15), 0 2px 6px rgba(255, 255, 255, 0.1), 0 1px 2px rgba(255, 255, 255, 0.08)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
                overflow: 'hidden',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-10px)';
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(55, 55, 55, 1) 0%, rgba(45, 45, 45, 1) 100%)';
                e.currentTarget.style.boxShadow = '0 20px 40px rgba(255, 255, 255, 0.3), 0 10px 20px rgba(255, 255, 255, 0.22), 0 5px 10px rgba(255, 255, 255, 0.15), 0 2px 4px rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.35)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(45, 45, 45, 1) 0%, rgba(35, 35, 35, 1) 100%)';
                e.currentTarget.style.boxShadow = '0 10px 25px rgba(255, 255, 255, 0.2), 0 5px 12px rgba(255, 255, 255, 0.15), 0 2px 6px rgba(255, 255, 255, 0.1), 0 1px 2px rgba(255, 255, 255, 0.08)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
              }}
            >
              {/* Shine effect overlay */}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                  transition: 'left 0.5s ease',
                  pointerEvents: 'none',
                }}
                className='shine-effect'
              />

              <h2
                style={{
                  fontSize: 'clamp(1.5rem, 2.5vw, 2rem)',
                  fontWeight: '700',
                  color: '#FFFFFF',
                  marginBottom: '0.5rem',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                  position: 'relative',
                  lineHeight: '1.2',
                }}
              >
                {card.title}
              </h2>
              <p
                style={{
                  fontSize: 'clamp(0.95rem, 1.5vw, 1.1rem)',
                  color: 'rgba(255, 255, 255, 0.85)',
                  fontWeight: '400',
                  margin: '0',
                  letterSpacing: '0.3px',
                  textShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
                  position: 'relative',
                  lineHeight: '1.5',
                }}
              >
                {card.subtitle}
              </p>
            </div>
          ))}
        </div>
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
            @supports (backdrop-filter: blur(15px)) or (-webkit-backdrop-filter: blur(15px)) {
              .glass-card {
                backdrop-filter: blur(15px) saturate(150%);
                -webkit-backdrop-filter: blur(15px) saturate(150%);
              }
            }
            
            .glass-card:hover .shine-effect {
              left: 100% !important;
            }

            .home-carousel-category {
              animation: home-carousel-category-glow 2.8s ease-in-out infinite;
            }

            @keyframes home-carousel-category-glow {
              0%, 100% {
                filter: brightness(1);
              }
              50% {
                filter: brightness(1.18);
              }
            }

            .home-carousel-wrap {
              overflow: visible !important;
            }

            .home-carousel-wrap .swiper {
              overflow: visible !important;
              padding: 16px 0 10px;
            }

            .home-carousel-slide {
              height: auto !important;
              overflow: visible !important;
              padding-top: 0 !important;
              padding-bottom: 0 !important;
            }

            .home-carousel-custom-card {
              width: min(100%, 960px);
              min-height: 270px;
              margin: 0 auto;
              overflow: hidden;
              border-radius: 22px;
              border: 1px solid rgba(var(--home-carousel-accent-rgb), 0.52);
              background: rgba(8, 8, 12, 0.92);
              box-shadow: 0 24px 60px rgba(0, 0, 0, 0.42), 0 0 24px rgba(var(--home-carousel-accent-rgb), 0.18);
            }

            .home-carousel-custom-card img,
            .home-carousel-custom-card video {
              max-width: 100%;
              height: auto;
            }

            .home-carousel-custom-card a {
              color: inherit;
            }

            .home-carousel-card:hover {
              transform: translateY(-10px);
              background: radial-gradient(circle at 85% 0%, rgba(var(--home-carousel-accent-rgb), 0.18) 0%, transparent 34%), linear-gradient(135deg, rgba(28, 28, 34, 0.98) 0%, rgba(10, 10, 14, 0.96) 100%) !important;
              box-shadow: 0 30px 74px rgba(0, 0, 0, 0.54), 0 0 42px rgba(var(--home-carousel-accent-rgb), 0.34), 0 0 0 1px rgba(var(--home-carousel-accent-rgb), 0.62), inset 0 1px 0 rgba(var(--home-carousel-accent-rgb), 0.32) !important;
              border-color: rgba(var(--home-carousel-accent-rgb), 0.78) !important;
            }

            .home-carousel-footer {
              position: relative;
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 0.75rem;
              margin-top: 1.5rem;
              width: 100%;
            }

            .home-carousel-cta {
              flex-shrink: 0;
              margin-left: auto;
            }

            .home-special-price::before {
              content: none;
            }

            .home-special-price-spinner {
              animation: home-special-spin 14s linear infinite;
              transform-origin: center center;
              will-change: transform;
            }

            .home-carousel-card:hover .home-special-price {
              transform: translateY(-2px) scale(1.04);
              filter: drop-shadow(0 6px 12px rgba(0, 0, 0, 0.4));
            }

            .home-carousel-card:hover .home-special-price-spinner {
              animation-duration: 8s;
            }

            @keyframes home-special-spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }

            .home-carousel-status {
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 10px;
              margin-top: 18px;
            }

            .home-carousel-dot {
              width: 9px;
              height: 9px;
              padding: 0;
              border: 0;
              border-radius: 50%;
              background: rgba(255, 255, 255, 0.35);
              cursor: pointer;
              transition: all 0.2s ease;
            }

            .home-carousel-dot:hover,
            .home-carousel-dot.is-active {
              background: #FFFFFF;
              transform: scale(1.35);
            }
            
            @media (min-width: 992px) {
              .cards-container {
                display: grid !important;
                grid-template-columns: repeat(2, 1fr) !important;
                flex-direction: row !important;
              }
            }
            
            @media (max-width: 768px) {
              .container > div {
                padding: 1.5rem 0.5rem !important;
              }
              
              .glass-card {
                padding: 2rem 1.5rem !important;
              }

              .home-carousel-card,
              .home-carousel-custom-card {
                max-height: 340px !important;
              }

              .home-carousel-footer {
                margin-top: 0.85rem;
                gap: 0.5rem;
              }

              .home-special-price {
                width: 96px !important;
                height: 96px !important;
              }

              .home-special-price-label {
                font-size: 0.58rem !important;
                letter-spacing: 0.08em !important;
              }

              .home-special-price-current {
                font-size: 1.02rem !important;
              }

              .home-special-price-original {
                font-size: 0.58rem !important;
              }

              .home-carousel-cta {
                font-size: 0.8rem !important;
                padding: 0.5rem 0.85rem !important;
                border-radius: 8px !important;
              }

              .home-course-highlights {
                grid-template-columns: 1fr !important;
              }
            }
            
            @media (max-width: 480px) {
              .glass-card {
                padding: 1.5rem 1rem !important;
              }

              .home-special-price {
                width: 90px !important;
                height: 90px !important;
              }

              .home-special-price-label {
                font-size: 0.54rem !important;
              }

              .home-special-price-current {
                font-size: 0.95rem !important;
              }

              .home-carousel-cta {
                font-size: 0.75rem !important;
                padding: 0.45rem 0.7rem !important;
              }
            }
          `,
        }}
      />
    </section>
  );
}
